import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, formatApiError } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const AdminPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState({});
  const [colors, setColors] = useState({});
  const [fonts, setFonts] = useState({});
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newDiscount, setNewDiscount] = useState("");
  const [creatingCode, setCreatingCode] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`);
      setSettings(res.data);
      setPricing(res.data.pricing || {});
      setColors(res.data.colors || {});
      setFonts(res.data.fonts || {});
      setDiscountCodes(res.data.discount_codes || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { pricing, colors, fonts });
      toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const createDiscount = async (e) => {
    e.preventDefault();
    if (!newCode || !newDiscount) return;
    setCreatingCode(true);
    const formData = new FormData();
    formData.append("code", newCode);
    formData.append("discount_percent", newDiscount);
    try {
      await axios.post(`${API}/admin/discount-codes`, formData);
      toast.success("Created");
      setNewCode("");
      setNewDiscount("");
      fetchSettings();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreatingCode(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <Loader2 className="w-6 h-6 animate-spin text-cyan-400/40" />
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/dashboard")} className="text-cyan-400/40 hover:text-cyan-300 transition-colors" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <h1 className="font-['Rajdhani'] text-lg tracking-wide">ADMIN</h1>
          </div>
          <Button onClick={saveSettings} className="h-10 px-6 rounded-sm btn-primary font-['Rajdhani'] tracking-wider text-sm" disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />SAVE</>}
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12">
        <Tabs defaultValue="pricing" className="space-y-8">
          <TabsList className="bg-transparent border-b border-cyan-500/10 rounded-none p-0 h-auto space-x-8">
            {["pricing", "colors", "fonts", "discounts"].map(t => (
              <TabsTrigger key={t} value={t} className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-cyan-300 data-[state=active]:border-b-2 data-[state=active]:border-cyan-400 rounded-none px-0 pb-4 text-cyan-200/40 capitalize font-['Rajdhani'] tracking-wider" data-testid={`tab-${t}`}>{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="pricing" className="mt-8">
            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl">
              {[["aspect_ratio", "Aspect Ratio", 4.99], ["restripe", "Restripe", 2.99], ["encoding", "Encoding", 0.99], ["project_restoration", "Restoration", 9.99]].map(([key, label, def]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-sm text-cyan-300/60">{label}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-500/30">$</span>
                    <Input type="number" step="0.01" value={pricing[key] || def} onChange={e => setPricing({ ...pricing, [key]: parseFloat(e.target.value) })} className="h-10 pl-7 rounded-sm" data-testid={`price-${key}`} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="colors" className="mt-8">
            <div className="grid sm:grid-cols-3 gap-6 max-w-2xl">
              {[["primary", "#00a8ff"], ["accent", "#00d4ff"], ["background", "#020a14"]].map(([key, def]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-sm text-cyan-300/60 capitalize">{key}</Label>
                  <div className="flex gap-2">
                    <Input type="color" value={colors[key] || def} onChange={e => setColors({ ...colors, [key]: e.target.value })} className="w-10 h-10 p-0.5 rounded-sm" data-testid={`color-${key}`} />
                    <Input type="text" value={colors[key] || def} onChange={e => setColors({ ...colors, [key]: e.target.value })} className="h-10 rounded-sm flex-1" />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="fonts" className="mt-8">
            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl">
              {[["heading", "Rajdhani"], ["body", "Inter"]].map(([key, def]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-sm text-cyan-300/60 capitalize">{key}</Label>
                  <Input type="text" value={fonts[key] || def} onChange={e => setFonts({ ...fonts, [key]: e.target.value })} className="h-10 rounded-sm" data-testid={`font-${key}`} />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="discounts" className="mt-8 space-y-8">
            <form onSubmit={createDiscount} className="flex gap-4 max-w-md">
              <Input placeholder="Code" value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())} className="h-10 rounded-sm flex-1" data-testid="new-discount-code" />
              <Input type="number" placeholder="%" value={newDiscount} onChange={e => setNewDiscount(e.target.value)} className="h-10 w-20 rounded-sm" data-testid="new-discount-percent" />
              <Button type="submit" className="h-10 px-4 rounded-sm btn-primary" disabled={creatingCode} data-testid="create-discount-btn">
                {creatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </form>
            {discountCodes.length > 0 ? (
              <div className="border-t border-cyan-500/10 pt-6">
                {discountCodes.map((dc, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-cyan-500/10">
                    <span className="font-mono text-cyan-200/80">{dc.code}</span>
                    <div className="flex items-center gap-6">
                      <span className="text-cyan-200/60">{dc.discount_percent}%</span>
                      <span className={`text-sm ${dc.active ? 'text-green-400' : 'text-cyan-200/30'}`}>{dc.active ? 'Active' : 'Expired'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-cyan-200/30 text-sm">No discount codes</p>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
